/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from "@/shared/state";
import { produce } from "immer";

import type { LoadingContextType } from "./LoadingContext.types";

/**
 * Zustand-based loading context with queue management and minimum display time.
 */
export const loadingContext = createLocalZustand<LoadingContextType>(
  (initializer) => (set, get) => ({
    set: set,
    get: get,
    addLoading: () => {
      set(
        produce((state) => {
          const wasEmpty = state.loadingQueue === 0;
          state.loadingQueue += 1;
          
          if (wasEmpty) {
            state.loadingStartTime = Date.now();
            state.isLoading = true;
            state.showCornerPill = false;
          }
        })
      );
    },
    /**
     * Calculates time until loading should be hidden.
     * 
     * @param {number} startTime - Loading start time
     * @param {number} now - Current time
     * @returns {number} Time in milliseconds
     */
    calculateHideDelay: (startTime: number | null, now: number): number => {
      const HIDE_DELAY = 300;
      const MIN_DISPLAY_TIME = 500;
      
      if (!startTime) {
        return HIDE_DELAY;
      }
      
      const elapsedTime = now - startTime;
      if (elapsedTime >= MIN_DISPLAY_TIME) {
        return HIDE_DELAY;
      }
      
      return Math.max(0, MIN_DISPLAY_TIME - elapsedTime) + HIDE_DELAY;
    },

    /**
     * Hides loading state if queue is empty.
     * 
     * @returns {void}
     */
    hideLoadingIfQueueEmpty: () => {
      const finalState = get();
      if (finalState.loadingQueue > 0 || !finalState.isLoading) return;
      set(
        produce((currentState) => {
          if (currentState.loadingQueue > 0) return;
          currentState.isLoading = false;
          currentState.loadingStartTime = null;
          if (currentState.isRequestLoading) currentState.showCornerPill = true;
        })
      );
    },

    /**
     * Schedules hiding of loading after delay.
     * 
     * @param {number} timeUntilHide - Time to wait before hiding
     * @returns {void}
     */
    scheduleHideLoading: function(timeUntilHide: number) {
      setTimeout(() => {
        const currentState = get();
        currentState.hideLoadingIfQueueEmpty();
      }, timeUntilHide);
    },

    /**
     * Removes one item from the loading queue and hides loading when queue is empty.
     * Includes a delay after all requests complete to improve UX.
     *
     * @returns {void}
     */
    removeLoading: function() {
      set(
        produce((currentState) => {
          if (currentState.loadingQueue > 0) {
            currentState.loadingQueue -= 1;
          }
        })
      );

      setTimeout(() => {
        const state = get();
        
        if (!state.isLoading) {
          return;
        }

        if (state.loadingQueue > 0) {
          return;
        }

        const now = Date.now();
        const timeUntilHide = state.calculateHideDelay(state.loadingStartTime, now);
        state.scheduleHideLoading(timeUntilHide);
      }, 0);
    },
    loadingQueue: 0,
    isLoading: process.env.NODE_ENV !== 'test',
    loadingStartTime: process.env.NODE_ENV === 'test' ? null : Date.now(),
    requestQueue: 0,
    isRequestLoading: false,
    requestStartTime: null,
    showCornerPill: false,
    addRequestLoading: () => {
      set(
        produce((state) => {
          const wasEmpty = state.requestQueue === 0;
          state.requestQueue += 1;
          if (wasEmpty) {
            state.requestStartTime = Date.now();
            state.isRequestLoading = true;
            state.showCornerPill = !state.isLoading;
          }
        })
      );
    },
    hideRequestLoadingIfEmpty: function() {
      const finalState = get();
      if (finalState.requestQueue > 0 || !finalState.isRequestLoading) return;
      set(produce((s) => {
        s.isRequestLoading = false;
        s.requestStartTime = null;
        s.showCornerPill = false;
      }));
    },
    removeRequestLoading: function() {
      set(
        produce((currentState) => {
          if (currentState.requestQueue > 0) currentState.requestQueue -= 1;
        })
      );

      setTimeout(() => {
        const state = get();
        if (!state.isRequestLoading || state.requestQueue > 0) return;
        const timeUntilHide = state.calculateHideDelay(state.requestStartTime, Date.now());
        setTimeout(() => state.hideRequestLoadingIfEmpty(), timeUntilHide);
      }, 0);
    },
  })
);

export const { useStore: useLoading } = loadingContext;
